import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_BASE_URL } from './service-proxies';

// Hand-written proxy for HostCockpitAppService (plain JSON, no NSwag class wrappers).

export interface HostCockpitOnboardingStep {
    name: string;
    done: boolean;
    count: number;
}

export interface HostCockpitTenant {
    tenantId: number;
    tenancyName: string;
    name: string;
    editionName: string;
    isActive: boolean;
    creationTime: string;
    ageDays: number;
    onboardingStatus: string;
    onboardingPercent: number;
    steps: HostCockpitOnboardingStep[];
    activeUsers: number;
    activeEmployees: number;
    userLimit: number;
    lastLoginTime: string;
    logins30Days: number;
    lastPunchTime: string;
    subscriptionEndDate: string;
    pendingApprovals: number;
    oldestPendingApprovalDays: number;
    signedUpEmployees: number;
    signUpPercent: number;
    leaveRequests30Days: number;
    leavePending30Days: number;
    manualLeaves30Days: number;
    salaryFrozenMonths: number;
    salaryStatus: string;
    health: string;
    healthReasons: string[];
}

export interface HostCockpitAlert {
    tenantId: number;
    tenancyName: string;
    type: string;
    severity: string;
    message: string;
}

export interface HostCockpitSummary {
    totalTenants: number;
    activeTenants: number;
    onboardingInProcess: number;
    onboardingStalled: number;
    onboardingCompleted: number;
    healthyTenants: number;
    warningTenants: number;
    inactiveTenants: number;
    totalAlerts: number;
    highAlerts: number;
}

export interface HostCockpitOutput {
    generatedAt: string;
    approvalAgeingDays: number;
    inactiveAfterDays: number;
    summary: HostCockpitSummary;
    tenants: HostCockpitTenant[];
    alerts: HostCockpitAlert[];
}

export interface HostCockpitTrendPoint {
    date: string;
    count: number;
}

export interface HostCockpitApprovalBucket {
    label: string;
    count: number;
}

export interface HostCockpitTenantDetail {
    punchTrend: HostCockpitTrendPoint[];
    approvalAgeing: HostCockpitApprovalBucket[];
    totalPunches30Days: number;
    activeDays30: number;
}

@Injectable()
export class HostCockpitServiceProxy {
    private baseUrl: string;

    constructor(private http: HttpClient, @Optional() @Inject(API_BASE_URL) baseUrl?: string) {
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
    }

    getCockpit(): Observable<HostCockpitOutput> {
        return this.http
            .get<any>(this.baseUrl + '/api/services/app/HostCockpit/GetCockpit')
            .pipe(map(r => r.result as HostCockpitOutput));
    }

    getTenantDetail(tenantId: number): Observable<HostCockpitTenantDetail> {
        return this.http
            .get<any>(this.baseUrl + '/api/services/app/HostCockpit/GetTenantDetail?tenantId=' + encodeURIComponent('' + tenantId))
            .pipe(map(r => r.result as HostCockpitTenantDetail));
    }
}
